import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getSubdomainUrl, isPWAStandalone } from '../utils/links'
import { FaShoppingCart } from 'react-icons/fa'
import { useCart } from '../context/CartContext'

const NAV_ITEMS = [
  { label: 'ABOUT', to: '/about' },
  { label: 'BUSINESS UNITS', hasDropdown: true },
  { label: 'BLOG', to: '/blog' },
  { label: 'CONTACT', href: '#contact' },
]

const DROPDOWN_ITEMS = [
  { label: 'Architecture & Engineering', subdomain: 'architecture' },
  { label: 'Construction', subdomain: 'construction' },
  { label: 'Impact Windows', subdomain: 'windows' },
  { label: 'Roofing', subdomain: 'roofing' },

]

const NAV_LINK_BASE =
  'nav-font px-5 py-3 text-[0.9rem] font-semibold uppercase tracking-[0.12em] transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-dark'

const getNavClasses = (isActive) =>
  `${NAV_LINK_BASE} ${isActive ? 'bg-primary text-ink shadow-sm' : 'text-ink hover:bg-primary hover:text-ink'}`

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownTimer = useRef(null)
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { totalUnits, setIsCartOpen } = useCart()
  const navigate = useNavigate()

  const handleCartClick = () => {
    if (totalUnits > 0) {
      setIsCartOpen(true)
    } else {
      navigate('/shop')
    }
  }

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language.startsWith('en') ? 'es' : 'en')
  }

  const cancelDropdownTimer = () => {
    if (dropdownTimer.current) {
      clearTimeout(dropdownTimer.current)
      dropdownTimer.current = null
    }
  }

  const openDropdown = () => {
    cancelDropdownTimer()
    setIsDropdownOpen(true)
  }

  const closeDropdownWithDelay = () => {
    cancelDropdownTimer()
    dropdownTimer.current = setTimeout(() => {
      setIsDropdownOpen(false)
    }, 150)
  }

  useEffect(
    () => () => {
      cancelDropdownTimer()
    },
    [],
  )

  return (
    <nav className="fixed top-0 z-50 w-full h-20 bg-white border-b border-ink/10" aria-label="Primary">
      <div className="px-4 mx-auto max-w-7xl h-full sm:px-6 lg:px-8">
        <div className="flex justify-between h-full">
          {/* In dev/PWA: always use Link for SPA navigation. In prod on main domain: Link to '/'. In prod on subdomain (browser): anchor to main domain. */}
          {!import.meta.env.PROD || isPWAStandalone || window.location.hostname === 'jdrivero.com' || window.location.hostname === 'www.jdrivero.com' ? (
            <Link to="/" className="flex items-center" aria-label="JD Rivero home" onClick={() => setIsMenuOpen(false)}>
              <img src="/logo-black.webp" alt="JD Rivero logo" width="64" height="64" className="object-contain w-16 h-16" loading="eager" />
            </Link>
          ) : (
            <a href="https://jdrivero.com" className="flex items-center" aria-label="JD Rivero home">
              <img src="/logo-black.webp" alt="JD Rivero logo" width="64" height="64" className="object-contain w-16 h-16" loading="eager" />
            </a>
          )}

          <div className="hidden items-center space-x-2 md:flex">
            <Link to="/about" className={getNavClasses(location.pathname === '/about')}>
              {t('nav.about')}
            </Link>

            <div className="relative group" onMouseEnter={openDropdown} onMouseLeave={closeDropdownWithDelay}>
              <button
                className={`${getNavClasses(DROPDOWN_ITEMS.some((item) => location.pathname.includes(item.subdomain) || window.location.hostname.startsWith(item.subdomain)))} flex items-center gap-1`}
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
                onClick={() => {
                  cancelDropdownTimer()
                  setIsDropdownOpen((prev) => !prev)
                }}
                onFocus={openDropdown}
                onBlur={closeDropdownWithDelay}
              >
                {t('nav.units')}
                <svg className="w-4 h-4 text-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute left-0 z-10 py-2 mt-2 w-56 bg-white border shadow-lg border-ink/10">
                  {DROPDOWN_ITEMS.map((item) => {
                    const url = item.subdomain ? getSubdomainUrl(item.subdomain) : item.href
                    const isExternal = url.startsWith('http')
                    const linkClass = "block px-4 py-2 text-ink hover:bg-primary-light hover:text-ink transition-colors"
                    const label = item.subdomain ? t(`nav.${item.subdomain}`) : item.label
                    return isExternal ? (
                      <a
                        key={item.label}
                        href={url}
                        className={linkClass}
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        {label}
                      </a>
                    ) : (
                      <Link
                        key={item.label}
                        to={url}
                        className={linkClass}
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        {label}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>

            <Link to="/blog" className={getNavClasses(location.pathname.startsWith('/blog'))}>
              {t('nav.blog') || 'BLOG'}
            </Link>

            <Link to="/contact" className={getNavClasses(location.pathname === '/contact')}>
              {t('nav.contact')}
            </Link>

            <button onClick={handleCartClick} className="relative p-2 ml-4 transition-colors text-ink hover:text-primary" aria-label="Cart">
              <FaShoppingCart className="w-5 h-5" />
              {totalUnits > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">{totalUnits}</span>
              )}
            </button>

            <button
              onClick={toggleLanguage}
              className="px-3 py-1 ml-1 text-sm font-semibold rounded border transition-colors border-ink/20 hover:bg-gray-100"
            >
              {i18n.language.startsWith('en') ? 'ES' : 'EN'}
            </button>
          </div>

          <div className="flex gap-3 items-center md:hidden">
            <button onClick={handleCartClick} className="relative p-1 transition-colors text-ink hover:text-primary" aria-label="Cart">
              <FaShoppingCart className="w-5 h-5" />
              {totalUnits > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">{totalUnits}</span>
              )}
            </button>

            <button
              onClick={toggleLanguage}
              className="px-2 py-1 text-sm font-semibold rounded border transition-colors border-ink/20 hover:bg-gray-100"
            >
              {i18n.language.startsWith('en') ? 'ES' : 'EN'}
            </button>
            <button
              onClick={() => setIsMenuOpen((prev) => !prev)}
              className="text-ink"
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="bg-white border-t md:hidden border-ink/10">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link to="/about" className={`${getNavClasses(location.pathname === '/about')} block`} onClick={() => setIsMenuOpen(false)}>
              {t('nav.about')}
            </Link>

            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                className={`${getNavClasses(DROPDOWN_ITEMS.some((item) => location.pathname.includes(item.subdomain) || window.location.hostname.startsWith(item.subdomain)))} w-full flex items-center justify-between`}
                aria-expanded={isDropdownOpen}
              >
                <span>{t('nav.units')}</span>
                <svg
                  className={`w-4 h-4 text-ink transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="py-2 mt-2 bg-white border shadow-lg border-ink/10">
                  {DROPDOWN_ITEMS.map((item) => {
                    const url = item.subdomain ? getSubdomainUrl(item.subdomain) : item.href
                    const isExternal = url.startsWith('http')
                    const linkClass = "block px-4 py-2 text-ink hover:bg-primary-light hover:text-ink transition-colors"
                    const label = item.subdomain ? t(`nav.${item.subdomain}`) : item.label
                    const handleClick = () => { setIsDropdownOpen(false); setIsMenuOpen(false) }
                    return isExternal ? (
                      <a key={item.label} href={url} className={linkClass} onClick={handleClick}>
                        {label}
                      </a>
                    ) : (
                      <Link key={item.label} to={url} className={linkClass} onClick={handleClick}>
                        {label}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>

            <Link to="/blog" className={`${getNavClasses(location.pathname.startsWith('/blog'))} block`} onClick={() => setIsMenuOpen(false)}>
              {t('nav.blog') || 'BLOG'}
            </Link>
            <Link to="/contact" className={`${getNavClasses(location.pathname === '/contact')} block`} onClick={() => setIsMenuOpen(false)}>
              {t('nav.contact')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navigation
